import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = { green: 'Придобита', yellow: 'В процес', red: 'Не придобита' };
const STATUS_DOT = { green: 'bg-green-500', yellow: 'bg-yellow-400', red: 'bg-red-500' };
const STATUS_TEXT = { green: 'text-green-700', yellow: 'text-yellow-700', red: 'text-red-600' };
const STATUS_BG   = { green: 'bg-green-50 border-green-200', yellow: 'bg-yellow-50 border-yellow-200', red: 'bg-red-50 border-red-200' };

export default function MyCompetenciesPage({ token, studentId, classIds = [] }) {
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch competency groups for each of the student's classes
        const allGroups = [];
        for (const classId of classIds) {
          const res = await axios.get(`${API_URL}/competency-groups`, { 
            headers, 
            params: { class_id: classId } 
          });
          allGroups.push(...res.data);
        }
        
        // Fetch student's assignments
        const assignmentsRes = await axios.get(`${API_URL}/competencies/student/${studentId}`, { headers });
        
        setGroups(allGroups);
        const map = {};
        assignmentsRes.data.forEach(a => { map[a.item_id] = a.status; });
        setAssignments(map);
      } catch {
        toast.error('Грешка при зареждане');
      } finally {
        setLoading(false);
      }
    };
    if (classIds.length > 0) {
      fetchAll();
    } else {
      setLoading(false);
    }
  }, [token, studentId, classIds]);

  if (loading) return <p className="text-slate-500 text-sm">Зареждане...</p>;

  // Summary counts across all groups
  const allItemIds = groups.flatMap(g => g.items.map(i => i.item_id));
  const totalAssigned = allItemIds.filter(id => assignments[id]).length;
  const totalGreen  = allItemIds.filter(id => assignments[id] === 'green').length;
  const totalYellow = allItemIds.filter(id => assignments[id] === 'yellow').length;
  const totalRed    = allItemIds.filter(id => assignments[id] === 'red').length;
  const totalItems  = allItemIds.length;

  return (
    <div className="space-y-5" data-testid="my-competencies-section">
      <h2 className="text-xl font-semibold text-slate-900">Моите компетентности</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3 text-center">
          <div className="text-2xl font-bold text-slate-700">{totalItems}</div>
          <div className="text-xs text-slate-500 mt-0.5">Общо</div>
        </Card>
        <Card className="p-3 text-center bg-green-50 border-green-200">
          <div className="text-2xl font-bold text-green-600">{totalGreen}</div>
          <div className="text-xs text-green-600 mt-0.5">Придобити</div>
        </Card>
        <Card className="p-3 text-center bg-yellow-50 border-yellow-200">
          <div className="text-2xl font-bold text-yellow-600">{totalYellow}</div>
          <div className="text-xs text-yellow-600 mt-0.5">В процес</div>
        </Card>
        <Card className="p-3 text-center bg-red-50 border-red-200">
          <div className="text-2xl font-bold text-red-600">{totalRed}</div>
          <div className="text-xs text-red-600 mt-0.5">Не придобити</div>
        </Card>
      </div>

      {/* Progress bar */}
      {totalAssigned > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-slate-500">
            <span>Напредък</span>
            <span>{Math.round((totalGreen / totalItems) * 100)}% придобити</span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden flex">
            <div className="bg-green-500 h-full transition-all" style={{ width: `${(totalGreen / totalItems) * 100}%` }} />
            <div className="bg-yellow-400 h-full transition-all" style={{ width: `${(totalYellow / totalItems) * 100}%` }} />
            <div className="bg-red-500 h-full transition-all" style={{ width: `${(totalRed / totalItems) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Groups */}
      {groups.map(group => {
        // Only show groups where student has at least one assignment OR always show all
        const groupItems = group.items;
        if (groupItems.length === 0) return null;

        return (
          <Card key={group.group_id} className="overflow-hidden" data-testid={`competency-group-${group.group_id}`}>
            <div className="flex items-center gap-2 px-4 py-3 border-b bg-slate-50">
              <h3 className="font-semibold text-slate-800">{group.name}</h3>
              <Badge className="text-xs bg-blue-100 text-blue-700 font-normal">{group.subject}</Badge>
            </div>
            <div className="divide-y">
              {groupItems.map(item => {
                const status = assignments[item.item_id] ?? null;
                return (
                  <div
                    key={item.item_id}
                    className={`flex items-center gap-3 px-4 py-2.5 ${status ? STATUS_BG[status] : ''}`}
                    data-testid={`comp-item-${item.item_id}`}
                  >
                    {/* Status dot */}
                    <span className={`shrink-0 w-3 h-3 rounded-full ${status ? STATUS_DOT[status] : 'bg-slate-300'}`} />
                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      {item.sub_section && (
                        <span className="text-xs font-medium text-[#00966E] mr-1">[{item.sub_section}]</span>
                      )}
                      <span className="text-sm text-slate-700">{item.text}</span>
                    </div>
                    {/* Status label */}
                    {status && (
                      <span className={`text-xs font-semibold shrink-0 ${STATUS_TEXT[status]}`}>
                        {STATUS_LABELS[status]}
                      </span>
                    )}
                    {!status && (
                      <span className="text-xs text-slate-400 shrink-0 italic">не е оценена</span>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}

      {groups.length === 0 && (
        <p className="text-slate-400 text-sm text-center py-10">Няма добавени компетентности все още.</p>
      )}
    </div>
  );
}
